import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, 
  RefreshCw, 
  Truck,
  Signal,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom vehicle marker icon
const createVehicleIcon = (status) => {
  const color = status === "active" ? "#4ade80" : status === "maintenance" ? "#fbbf24" : "#ef4444";
  return L.divIcon({
    className: "custom-div-icon",
    html: `
      <div style="
        background: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid #0a0c0a;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 15px ${color}80;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0c0a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/>
          <path d="M15 18H9"/>
          <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/>
          <circle cx="17" cy="18" r="2"/>
          <circle cx="7" cy="18" r="2"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Map component that handles auto-centering
const MapController = ({ vehicles }) => {
  const map = useMap();
  
  useEffect(() => {
    if (vehicles.length > 0) {
      const bounds = L.latLngBounds(
        vehicles
          .filter(v => v.latitude && v.longitude)
          .map(v => [v.latitude, v.longitude])
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [vehicles, map]);
  
  return null;
};

export default function LiveTrackingPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Default center: Dar es Salaam, Tanzania
  const defaultCenter = [-6.7924, 39.2083];

  const fetchVehicleLocations = useCallback(async () => {
    try {
      const response = await apiClient.get("/tracking/vehicles");
      setVehicles(response.data);
    } catch (error) {
      console.error("Failed to fetch vehicle locations:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicleLocations();
  }, [fetchVehicleLocations]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchVehicleLocations, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchVehicleLocations]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchVehicleLocations();
  };

  const handleSimulate = async () => {
    try {
      await apiClient.post("/tracking/simulate");
      toast.success("Vehicle positions simulated");
      fetchVehicleLocations();
    } catch (error) {
      toast.error("Failed to simulate movement");
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { label: "Active", className: "bg-green-500/20 text-green-400 border-green-500/30" },
      maintenance: { label: "Maintenance", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
      out_of_service: { label: "Out of Service", className: "bg-red-500/20 text-red-400 border-red-500/30" }
    };
    return configs[status] || configs.active;
  };

  const activeVehicles = vehicles.filter(v => v.latitude && v.longitude);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="tracking-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-[500px] w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            LIVE TRACKING
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Real-time fleet location monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`rounded-sm font-mono text-xs ${autoRefresh ? "bg-primary text-primary-foreground" : "border-primary text-primary"}`}
            data-testid="auto-refresh-btn"
          >
            <Signal className={`w-4 h-4 mr-2 ${autoRefresh ? "animate-pulse" : ""}`} />
            {autoRefresh ? "LIVE" : "AUTO REFRESH"}
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-border text-foreground hover:bg-accent rounded-sm"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            REFRESH
          </Button>
          <Button
            onClick={handleSimulate}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-mono text-xs"
            data-testid="simulate-btn"
          >
            SIMULATE MOVEMENT
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Truck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold text-foreground">{activeVehicles.length}</p>
              <p className="text-[10px] font-mono text-muted-foreground">TRACKING</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-green-500/10 border border-green-500/30 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold text-foreground">
                {vehicles.filter(v => v.status === "active").length}
              </p>
              <p className="text-[10px] font-mono text-muted-foreground">ACTIVE</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold text-foreground">
                {vehicles.filter(v => v.status === "maintenance").length}
              </p>
              <p className="text-[10px] font-mono text-muted-foreground">MAINTENANCE</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-3 flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${autoRefresh ? "bg-green-500 animate-pulse" : "bg-gray-500"}`} />
            <div>
              <p className="text-sm font-heading font-bold text-foreground">
                {autoRefresh ? "LIVE" : "PAUSED"}
              </p>
              <p className="text-[10px] font-mono text-muted-foreground">STATUS</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map and Vehicle List */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Map */}
        <Card className="bg-card border-border rounded-sm lg:col-span-3 overflow-hidden" data-testid="tracking-map">
          <CardContent className="p-0">
            <div className="h-[500px] relative">
              <MapContainer
                center={defaultCenter}
                zoom={12}
                style={{ height: "100%", width: "100%" }}
                className="z-0"
              >
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                <MapController vehicles={activeVehicles} />
                {activeVehicles.map((vehicle) => (
                  <Marker
                    key={vehicle.id}
                    position={[vehicle.latitude, vehicle.longitude]}
                    icon={createVehicleIcon(vehicle.status)}
                    eventHandlers={{
                      click: () => setSelectedVehicle(vehicle)
                    }}
                  >
                    <Popup className="vehicle-popup">
                      <div className="p-2 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <Truck className="w-4 h-4 text-primary" />
                          <span className="font-heading font-bold text-foreground">
                            {vehicle.registration_number}
                          </span>
                        </div>
                        <div className="space-y-1 text-xs font-mono">
                          <p className="text-muted-foreground">
                            {vehicle.make} {vehicle.model} ({vehicle.year})
                          </p>
                          <p className="text-muted-foreground">
                            Mileage: {vehicle.mileage?.toLocaleString()} km
                          </p>
                          <p className="text-muted-foreground">
                            Lat: {vehicle.latitude?.toFixed(6)}
                          </p>
                          <p className="text-muted-foreground">
                            Lng: {vehicle.longitude?.toFixed(6)}
                          </p>
                        </div>
                        <Badge className={`mt-2 ${getStatusBadge(vehicle.status).className} rounded-sm text-[10px]`}>
                          {getStatusBadge(vehicle.status).label}
                        </Badge>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              
              {/* Map overlay info */}
              <div className="absolute top-4 left-4 z-[1000] bg-card/90 backdrop-blur-sm border border-border rounded-sm p-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono text-muted-foreground">
                    DAR ES SALAAM, TANZANIA
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle List */}
        <Card className="bg-card border-border rounded-sm" data-testid="vehicle-list">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide text-foreground">
              FLEET UNITS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[440px] overflow-y-auto">
              {vehicles.map((vehicle) => {
                const statusBadge = getStatusBadge(vehicle.status);
                return (
                  <div
                    key={vehicle.id}
                    className={`p-3 border-b border-border cursor-pointer transition-colors hover:bg-accent/50 ${
                      selectedVehicle?.id === vehicle.id ? "bg-accent/50" : ""
                    }`}
                    onClick={() => setSelectedVehicle(vehicle)}
                    data-testid={`vehicle-item-${vehicle.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-mono text-sm font-medium text-primary">
                          {vehicle.registration_number}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {vehicle.make} {vehicle.model}
                        </p>
                      </div>
                      <Badge className={`${statusBadge.className} rounded-sm text-[10px] border`}>
                        {statusBadge.label.charAt(0)}
                      </Badge>
                    </div>
                    {vehicle.latitude && vehicle.longitude && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        {vehicle.latitude.toFixed(4)}, {vehicle.longitude.toFixed(4)}
                      </div>
                    )}
                  </div>
                );
              })}
              {vehicles.length === 0 && (
                <div className="p-8 text-center text-muted-foreground font-mono text-sm">
                  No vehicles to track
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selected Vehicle Details */}
      {selectedVehicle && (
        <Card className="bg-card border-border rounded-sm border-primary/30" data-testid="selected-vehicle-details">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="font-heading text-sm tracking-wide text-primary flex items-center gap-2">
              <Truck className="w-4 h-4" />
              SELECTED UNIT: {selectedVehicle.registration_number}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Vehicle</p>
                <p className="text-sm font-medium text-foreground">{selectedVehicle.make} {selectedVehicle.model}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Year</p>
                <p className="text-sm font-medium text-foreground">{selectedVehicle.year}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Type</p>
                <p className="text-sm font-medium text-foreground">{selectedVehicle.vehicle_type}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Mileage</p>
                <p className="text-sm font-medium text-foreground">{selectedVehicle.mileage?.toLocaleString()} km</p>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Latitude</p>
                <p className="text-sm font-mono text-primary">{selectedVehicle.latitude?.toFixed(6)}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Longitude</p>
                <p className="text-sm font-mono text-primary">{selectedVehicle.longitude?.toFixed(6)}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Status</p>
                <Badge className={`${getStatusBadge(selectedVehicle.status).className} rounded-sm text-xs border mt-1`}>
                  {getStatusBadge(selectedVehicle.status).label}
                </Badge>
              </div>
              <div>
                <p className="text-xs font-mono text-muted-foreground uppercase">Last Update</p>
                <p className="text-xs font-mono text-muted-foreground">
                  {selectedVehicle.last_location_update ? new Date(selectedVehicle.last_location_update).toLocaleString() : "-"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
